<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Email Lists</h1>
        <p class="text-gray-600">Manage your email lists, clubs, and subscribers</p>
      </div>
      <button
        @click="showCreateModal = true"
        class="btn-primary"
      >
        <PlusIcon class="w-5 h-5 mr-2" />
        Create List
      </button>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow-sm p-6">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label class="form-label">Partner</label>
          <select
            v-model="filters.partner"
            class="input-field"
          >
            <option value="all">All Partners</option>
            <option value="current">Current Partner</option>
          </select>
        </div>
        
        <div>
          <label class="form-label">Club</label>
          <select
            v-model="filters.club"
            class="input-field"
          >
            <option value="all">All Clubs</option>
            <option value="none">No Club</option>
            <option
              v-for="club in clubs"
              :key="club.ClubID"
              :value="club.ClubID"
            >
              {{ club.ClubName }}
            </option>
          </select>
        </div>
        
        <div>
          <label class="form-label">Search</label>
          <input
            v-model="filters.search"
            type="text"
            placeholder="List name or description..."
            class="input-field"
          />
        </div>
        
        <div class="flex items-end">
          <button
            @click="clearFilters"
            class="btn-secondary w-full"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>

    <!-- Lists by Partner and Club -->
    <div class="space-y-6">
      <div
        v-for="partnerGroup in groupedLists"
        :key="partnerGroup.partnerID"
        class="bg-white rounded-lg shadow-sm"
      >
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900">
            {{ partnerGroup.partnerName }}
          </h2>
        </div>
        
        <div class="p-6">
          <div
            v-for="clubGroup in partnerGroup.clubs"
            :key="clubGroup.clubID"
            class="mb-8 last:mb-0"
          >
            <div v-if="clubGroup.clubName" class="mb-4">
              <h3 class="text-md font-medium text-primary-700 bg-primary-50 px-3 py-2 rounded-lg inline-block">
                {{ clubGroup.clubName }}
              </h3>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div
                v-for="list in clubGroup.lists"
                :key="list.ListID"
                class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-200"
              >
                <div class="flex items-start justify-between">
                  <div class="flex-1">
                    <h3 class="font-medium text-gray-900">{{ list.ListName }}</h3>
                    <p v-if="list.Description" class="text-sm text-gray-500 mt-1">
                      {{ list.Description }}
                    </p>
                    <div class="flex items-center space-x-2 mt-2">
                      <span class="text-sm text-gray-400">{{ list.TotalEmails }} subscribers</span>
                      <span v-if="list.ClubName" class="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                        {{ list.ClubName }}
                      </span>
                    </div>
                    <div class="text-xs text-gray-400 mt-1">
                      Updated {{ formatDate(list.UpdatedAt) }}
                    </div>
                  </div>
                  <div class="flex items-center space-x-2">
                    <button
                      @click="editList(list)"
                      class="text-primary-600 hover:text-primary-700 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      @click="viewList(list)"
                      class="text-primary-600 hover:text-primary-700 text-sm font-medium"
                    >
                      View
                    </button>
                    <button
                      @click="deleteList(list.ListID)"
                      class="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit List Modal -->
    <ListModal
      v-if="showCreateModal"
      :list="editingList"
      :clubs="clubs"
      @close="closeModal"
      @saved="onListSaved"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { listsApi } from '../services/api'
import { useClubsStore } from '../stores/clubs'
import { toast } from 'vue3-toastify'
import {
  PlusIcon
} from '@heroicons/vue/24/outline'
import ListModal from '../components/ListModal.vue'

const router = useRouter()
const clubsStore = useClubsStore()

// State
const emailLists = ref([])
const loading = ref(false)
const showCreateModal = ref(false)
const editingList = ref(null)

// Filters
const filters = ref({
  partner: 'current',
  club: 'all',
  search: ''
})

// Computed
const filteredLists = computed(() => {
  let filtered = emailLists.value

  if (filters.value.search) {
    const search = filters.value.search.toLowerCase()
    filtered = filtered.filter(list => 
      list.ListName.toLowerCase().includes(search) ||
      (list.Description && list.Description.toLowerCase().includes(search))
    )
  }

  return filtered
})

const groupedLists = computed(() => {
  const groups = {}
  
  filteredLists.value.forEach(list => {
    const partnerID = list.PartnerID
    const clubID = list.ClubID || 'none'
    
    if (!groups[partnerID]) {
      groups[partnerID] = {
        partnerID,
        partnerName: list.PartnerName,
        clubs: {}
      }
    }
    
    if (!groups[partnerID].clubs[clubID]) {
      groups[partnerID].clubs[clubID] = {
        clubID,
        clubName: list.ClubName,
        lists: []
      }
    }
    
    groups[partnerID].clubs[clubID].lists.push(list)
  })
  
  // Convert to arrays and sort
  return Object.values(groups).map(partner => ({
    ...partner,
    clubs: Object.values(partner.clubs)
      .sort((a, b) => {
        if (a.clubID === 'none') return 1
        if (b.clubID === 'none') return -1
        return a.clubName.localeCompare(b.clubName)
      })
      .map(club => ({
        ...club,
        lists: club.lists.sort((a, b) => a.ListName.localeCompare(b.ListName))
      }))
  }))
})

const clubs = computed(() => clubsStore.clubs)

// Methods
const formatDate = (dateString) => {
  if (!dateString) return 'Never'
  return new Date(dateString).toLocaleDateString()
}

const fetchLists = async () => {
  try {
    loading.value = true
    const response = await listsApi.getAll()
    emailLists.value = response.data.data || []
  } catch (error) {
    console.error('Error fetching lists:', error)
    toast.error('Failed to fetch email lists')
  } finally {
    loading.value = false
  }
}

const clearFilters = () => {
  filters.value = {
    partner: 'current',
    club: 'all',
    search: ''
  }
}

const editList = (list) => {
  editingList.value = list
  showCreateModal.value = true
}

const viewList = (list) => {
  router.push(`/lists/${list.ListID}`)
}

const deleteList = async (listId) => {
  if (!confirm('Are you sure you want to delete this list? This action cannot be undone.')) {
    return
  }

  try {
    await listsApi.delete(listId)
    toast.success('List deleted successfully')
    await fetchLists()
  } catch (error) {
    console.error('Error deleting list:', error)
    toast.error('Failed to delete list')
  }
}

const closeModal = () => {
  showCreateModal.value = false
  editingList.value = null
}

const onListSaved = async () => {
  closeModal()
  await fetchLists()
  await clubsStore.fetchClubs()
  toast.success('List saved successfully')
}

// Lifecycle
onMounted(async () => {
  await fetchLists()
  await clubsStore.fetchClubs()
})
</script> 